import { useStore } from "@hq/client-react";
import { Button, type ButtonSize, type ButtonVariant, toast } from "@z/ui";
import { Download } from "lucide-react";
import { useState } from "react";
import { downloadExport, EXPORT_JSON_NAME, exportVault } from "./export";

export type ExportVaultButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
};

/**
 * One-click export of every note as a tree of `.md` files with front matter. Writes to a
 * folder the user picks where the browser allows it, otherwise downloads one JSON bundle.
 */
export function ExportVaultButton({
  variant = "secondary",
  size = "sm",
  className,
}: ExportVaultButtonProps) {
  const store = useStore();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { files } = await exportVault(store);
      const result = await downloadExport(files);
      if (result === "directory") {
        toast({ title: `Exported ${files.length} notes`, variant: "success" });
      } else if (result === "json") {
        toast({
          title: `Exported ${files.length} notes`,
          description: `This browser cannot write folders, so the tree was saved as ${EXPORT_JSON_NAME}.`,
          variant: "success",
        });
      }
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "danger",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      disabled={busy}
      {...(className === undefined ? {} : { className })}
      onClick={() => void run()}
    >
      <Download /> Export all notes
    </Button>
  );
}
